import { AuthState } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";
import React, { createContext, Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from "react";
import { getUserInfo } from "../api/mlbauth/UserInfoClient";
import { oktaAuth } from "../oktaAuth";
import { User } from "../types/User";
import { RoleConstants } from "../constants/RoleConstants";
import { PermissionConstants } from "../constants/PermissionConstants";
import RouteConstants from "../constants/RouteConstants";
import { datadogRum } from "@datadog/browser-rum";
import { isOAuthCallbackInProgress } from "../utils/OktaAuthUtil";

interface UserInfo extends User {
  accessToken: { accessToken: string; claims: { email: string }; expiresAt: number };
}

type UserContext = {
  userInfo: UserInfo;
  hasUserInfo: boolean;
  login?: (authState: AuthState) => void;
  logout: () => void;
  permissionError: boolean;
  loginFunction?: SetStateAction<void>;
  setPermissionError?: Dispatch<SetStateAction<boolean>>;
  setLoginFunction?: Dispatch<SetStateAction<void>>;
  getUserId?: () => number;
  isAdmin: () => boolean;
  isAuthenticator: () => boolean;
  isInventory: () => boolean;
  isMilestones: () => boolean;
  isLicensee: () => boolean;
  getDefaultRoute: () => RouteConstants;
};

const initialState: UserState = {
  userInfo: {
    accessToken: { accessToken: "", claims: { email: "" }, expiresAt: 0 },
    id: -1,
    trackviaAuthenticatorId: -1,
    username: "",
    firstName: "",
    lastName: "",
    fullName: "",
    role: "",
    orgId: -1,
    permissions: [],
  },
  loggedIn: false,
};

const UserContext = createContext<UserContext>({
  userInfo: initialState.userInfo,
  hasUserInfo: false,
  permissionError: false,
  logout: () => {
    console.log("logout");
  },
  isAdmin: (): boolean => false,
  isAuthenticator: (): boolean => false,
  isLicensee: (): boolean => false,
  isInventory: (): boolean => false,
  isMilestones: (): boolean => false,
  getDefaultRoute: () => RouteConstants.BASE,
});

interface UserState {
  userInfo: UserInfo;
  loggedIn: boolean;
}

type UserProviderProps = {
  children: React.ReactNode;
};

const UserProvider = ({ children }: UserProviderProps) => {
  const [userState, setUserState] = useState<UserState>(initialState);
  const [permissionError, setPermissionError] = useState<boolean>(false);
  const [loginFunction, setLoginFunction] = useState();
  const { authState } = useOktaAuth();
  const skipAutoLoginRef = useRef(false);

  const login = async (authState: AuthState) => {
    const userInfo = await getUserInfo();

    if (userInfo && authState.accessToken && authState.accessToken.claims.email) {
      setUserState({
        userInfo: {
          accessToken: { ...authState.accessToken, claims: { ...authState.accessToken.claims, email: "" } },
          ...userInfo,
        },
        loggedIn: true,
      });
    }
  };

  const logout = (): void => {
    // Do not wipe storage during the OAuth callback; that removes the PKCE codeVerifier.
    if (isOAuthCallbackInProgress()) {
      return;
    }
    skipAutoLoginRef.current = true;
    oktaAuth.tokenManager.clear();
    oktaAuth.transactionManager.clear();
    oktaAuth.authStateManager.updateAuthState();
    setUserState(initialState);
  };

  const getUserId = (): number => {
    return userState.userInfo.id;
  };

  const isAdmin = (): boolean => {
    return userState.userInfo.role === RoleConstants.SUPER_ADMIN;
  };

  const isAuthenticator = (): boolean => {
    return hasPermission(PermissionConstants.AUTHENTICATOR);
  };

  const isInventory = (): boolean => {
    return hasPermission(PermissionConstants.INVENTORY);
  };

  const isMilestones = (): boolean => {
    return hasPermission(PermissionConstants.MILESTONES);
  };

  const isLicensee = (): boolean => {
    return hasPermission(PermissionConstants.LICENSEE);
  };

  const hasPermission = (permission: PermissionConstants): boolean => {
    return userState.userInfo.permissions.includes(permission);
  };

  const getDefaultRoute = () => {
    if (!userState.loggedIn) return RouteConstants.BASE;
    if (isAdmin()) return RouteConstants.EYEBALL_SESSIONS;

    if (isAuthenticator()) {
      return RouteConstants.EYEBALL_SESSIONS;
    } else if (isLicensee()) {
      return RouteConstants.EYEBALL_SESSIONS_MANAGE;
    } else if (isInventory()) {
      return RouteConstants.INVENTORY;
    } else if (isMilestones()) {
      return RouteConstants.MILESTONES;
    } else {
      return RouteConstants.BASE;
    }
  };

  useEffect(() => {
    if (skipAutoLoginRef.current) {
      if (authState && !authState.isAuthenticated) {
        skipAutoLoginRef.current = false;
      }
      return;
    }
    if (authState?.isAuthenticated) {
      // when the user becomes authenticated, call onLogin() to populate AuthContext's user info
      login({ accessToken: authState.accessToken });
    }
  }, [authState?.isAuthenticated]);

  useEffect(() => {
    if (window.MLBBest.envVariables.ENV !== "local" && !!userState) {
      datadogRum.setUser({
        id: userState.userInfo.id.toString(),
        email: userState.userInfo.username,
        name: `${userState.userInfo.lastName}, ${userState.userInfo.firstName}`,
      });
    } else {
      datadogRum.clearUser();
    }
  }, [userState]);

  return (
    <UserContext.Provider
      value={{
        userInfo: userState.userInfo,
        hasUserInfo: userState.loggedIn,
        login,
        logout,
        permissionError,
        setPermissionError,
        loginFunction,
        setLoginFunction,
        getUserId,
        isAdmin,
        isAuthenticator,
        isInventory,
        isMilestones,
        isLicensee,
        getDefaultRoute,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

const useUser = (): UserContext => {
  const userContext = useContext<UserContext>(UserContext);
  if (userContext === undefined) {
    throw new Error(`useUser must be used within a UserProvider`);
  }
  return userContext;
};

export { oktaAuth } from "../oktaAuth";
export { UserContext, UserProvider, useUser, UserInfo };
